// @ts-nocheck
import React from 'react';
import { cleanUrl } from '../utils/url';
import { motion, useReducedMotion } from 'framer-motion';
import { MapPin, Star, ExternalLink, Heart, Eye, Share2, Bookmark, CheckCircle2, MoreHorizontal, Trash2, Link2, Copy, Clock, Youtube, Camera, Facebook } from 'lucide-react';
import { useSettings } from './SettingsContext';
import ListPicker from './ListPicker';
import { Video, VideoPlatform } from '../types';
import { enrichVideoWithPlaces } from '../lib/maps/enrichVideo';
import { searchPlacesServer, getPlaceDetailsServer } from '../services/placesSearch';
import { useToast } from './ToastProvider';
import StarRating from './StarRating';

// Hooks for menu a11y and outside dismiss
function useOutsideDismiss(open: boolean, ref: React.RefObject<HTMLElement>, onDismiss: () => void) {
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t)) onDismiss();
    };
    document.addEventListener('pointerdown', handler, { capture: true });
    return () => document.removeEventListener('pointerdown', handler, { capture: true } as any);
  }, [open, ref, onDismiss]);
}

function useMenuKeyboard(open: boolean, ref: React.RefObject<HTMLDivElement>, onClose: () => void) {
  React.useEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;
    const items: HTMLElement[] = Array.from(el.querySelectorAll('[role="menuitem"]')) as HTMLElement[];
    (items[0] || el).focus();
    const onKey = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      const idx = items.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); items[(idx + 1) % items.length]?.focus(); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, ref, onClose]);
}

// Platform icon helper
function getPlatformIcon(platform: string, className: string = "w-4 h-4") {
  switch (platform?.toLowerCase()) {
    case 'youtube': return <Youtube className={className} />;
    case 'tiktok': return <Camera className={className} />; // TikTok uses camera icon
    case 'instagram': return <Camera className={className} />; // Instagram uses camera icon
    case 'facebook': return <Facebook className={className} />;
    default: return <ExternalLink className={className} />;
  }
}

// Format date helper
function formatDateAdded(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString();
  } catch {
    return '';
  }
}

interface VideoCardProps {
  video: Video;
  onVideoClick: (video: Video) => void;
  index?: number;
  onToggleSelect?: (id: string, next: boolean) => void;
  selected?: boolean;
  userLatLng?: { lat: number; lng: number } | null;
  directionsPref?: 'google'|'apple'|'waze'|'uber';
  onDeleteVideo?: (id: string) => void;
}

const PLATFORM_LIST: readonly VideoPlatform[] = ['TikTok', 'Instagram', 'YouTube', 'Facebook'] as const;

const platformColors: Partial<Record<VideoPlatform, string>> = {
  TikTok: 'from-pink-500 to-red-500',
  Instagram: 'from-purple-500 to-pink-500',
  YouTube: 'from-red-500 to-red-600',
  Facebook: 'from-blue-500 to-blue-600',
};

const platformIcons: Partial<Record<VideoPlatform, string>> = {
  TikTok: '🎵',
  Instagram: '📸',
  YouTube: '▶️',
  Facebook: '👥',
};
const platformShort: Partial<Record<VideoPlatform, string>> = {
  YouTube: 'YT',
  TikTok: 'TT',
  Instagram: 'IG',
  Facebook: 'FB',
};
const platformRings: Partial<Record<VideoPlatform, string>> = {
  YouTube: 'ring-red-300',
  TikTok: 'ring-cyan-300',
  Instagram: 'ring-pink-300',
  Facebook: 'ring-blue-300',
};

const nf = new Intl.NumberFormat();

const FALLBACK_THUMB =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="450"><rect width="100%" height="100%" fill="#e5e7eb"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="22" fill="#6b7280">No preview</text></svg>`
  );

// Decode simple HTML entities that can appear in provider titles
const decodeEntities = (s = '') => {
  const d = document.createElement('textarea');
  d.innerHTML = s;
  return d.value;
};

// Minimal sanitizer for provider attribution snippets
function sanitizeAttribution(html: string) {
  try {
    const t = document.createElement('template');
    t.innerHTML = html;
    const allowed = new Set(['A','EM','STRONG','SPAN']);
    const walk = (el: Element) => {
      if (!allowed.has(el.tagName)) { el.replaceWith(...Array.from(el.childNodes)); return; }
      [...(el as any).attributes].forEach((a: any) => {
        const n = a.name.toLowerCase();
        const ok = (el.tagName === 'A' && (n === 'href' || n === 'title')) || n === 'class';
        if (!ok) (el as any).removeAttribute(a.name);
      });
      if (el.tagName === 'A') {
        (el as HTMLAnchorElement).rel = 'noopener noreferrer';
        (el as HTMLAnchorElement).target = '_blank';
      }
      el.querySelectorAll('*').forEach(walk);
    };
    t.content.querySelectorAll('*').forEach(walk);
    return t.innerHTML;
  } catch {
    return '';
  }
}

// Proxy IG/FB/TikTok CDN images via our /img function to avoid 403s
const proxify = (u?: string | null, ref?: string | null) => {
  if (!u || u === 'N/A') return undefined;
  try {
    const url = cleanUrl(u);
    const host = new URL(url).hostname;
    if (/(fbcdn|cdninstagram|fnafbcdn|tiktokcdn|instagram\.f|akamaized)/i.test(host)) {
      const sp = new URLSearchParams({ url });
  if (ref) sp.set('ref', ref);
  return `/img?${sp.toString()}`;
    }
  } catch {}
  return cleanUrl(u!);
};

// Allow only known image hosts or cdns; skip facebook.com/instagram.com page or video URLs
const IMAGE_HOST_RE = [
  /(^|\.)fbcdn\.net$/i,
  /(^|\.)cdninstagram\.com$/i,
  /(^|\.)fna\.fbcdn\.net$/i,
  /(^|\.)tiktokcdn(?:-[a-z]+)?\.com$/i,
  /(^|\.)i\.ytimg\.com$/i,
  /(^|\.)img\.youtube\.com$/i,
  /(^|\.)googleusercontent\.com$/i,
  /(^|\.)googleapis\.com$/i,
  /(^|\.)ggpht\.com$/i,
];
const allowImageHost = (h: string) => IMAGE_HOST_RE.some((re) => re.test(h));
const isImagePath = (p: string) => /\.(avif|webp|jpe?g|png|gif)$/i.test(p);

const preferImage = (u?: string | null, ref?: string | null) => {
  if (!u || u === 'N/A') return undefined;
  
  const raw = cleanUrl(u);
  
  // If it's already our proxy endpoint, use it as-is
  if (raw.startsWith('/img?')) {
    return raw;
  }
  
  try {
    const url = new URL(raw);
    
    // Fix old PhotoService URLs by extracting photo_reference or place_id
    if (url.hostname === 'maps.googleapis.com' && url.pathname.includes('/PhotoService.GetPhoto')) {
      const photoRef = url.searchParams.get('photoreference');
      if (photoRef) {
        return `/img?photo_ref=${encodeURIComponent(photoRef)}&w=800`;
      }
      // If no photo_reference, this old URL is broken - return undefined to use fallbacks
      return undefined;
    }
    
    // Skip obvious non-image hosts unless the path clearly has an image extension
    if (/(facebook\.com|instagram\.com)/i.test(url.hostname)) {
      return isImagePath(url.pathname) ? raw : undefined;
    }
    // Force proxying for Google Places photo hosts (can 302 to lh3.googleusercontent)
    if ((/\.(googleusercontent\.com|ggpht\.com)$/i.test(url.hostname)) || /(^|\.)googleapis\.com$/i.test(url.hostname)) {
      const sp = new URLSearchParams({ url: raw });
      const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
      if (ref) sp.set('ref', ref);
      else if (origin) sp.set('ref', origin);
      return `/img?${sp.toString()}`;
    }
    const approved = allowImageHost(url.hostname) || isImagePath(url.pathname);
    return approved ? proxify(raw, ref) : undefined;
  } catch {
    return undefined;
  }
};

const ytIdFromUrl = (u?: string) => {
  if (!u) return null;
  try {
    const url = new URL(u);
    if (url.hostname === 'youtu.be') return url.pathname.slice(1) || null;
    if (url.hostname.includes('youtube')) return url.searchParams.get('v');
  } catch {}
  return null;
};

const pickThumb = (v: Video, localPlaces?: string | null) =>
  preferImage(localPlaces || (v as any).places_photo_url, v.video_url) ||
  preferImage((v as any).provider_thumbnail_url, v.video_url) ||
  preferImage(v.thumbnail_url as any, v.video_url) ||
  (v.platform === 'YouTube' ? ((): string | undefined => {
    const id = ytIdFromUrl(v.video_url);
    return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : undefined;
  })() : undefined) ||
  undefined;

const VideoCard: React.FC<VideoCardProps> = ({ video, onVideoClick, index = 0, onToggleSelect, selected = false, userLatLng = null, directionsPref = 'google', onDeleteVideo }) => {
  const { settings } = useSettings();
  const lang = (settings?.language === 'en' ? 'en' : 'es');
  const [localPlacesPhoto, setLocalPlacesPhoto] = React.useState<string | null>(null);
  const [metaPatch, setMetaPatch] = React.useState<any>(null);
  const [isFav, setIsFav] = React.useState(false);
  const [shared, setShared] = React.useState<null | 'ok' | 'err'>(null);
  const [pressTimer, setPressTimer] = React.useState<any>(null);
  const [showListPicker, setShowListPicker] = React.useState(false);
  const [savedPulse, setSavedPulse] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const prefersReducedMotion = useReducedMotion();
  const menuPopupRef = React.useRef<HTMLDivElement | null>(null);
  useOutsideDismiss(showMenu, menuPopupRef, () => setShowMenu(false));
  useMenuKeyboard(showMenu, menuPopupRef, () => setShowMenu(false));
  // Normalize platform safely
  const rawPlatform = (video.platform ?? 'Other').toString();
  const isKnownPlatform = PLATFORM_LIST.includes(rawPlatform as VideoPlatform);
  const platform = isKnownPlatform ? (rawPlatform as VideoPlatform) : undefined;

  const badgeGradient = platform ? platformColors[platform] ?? 'from-gray-500 to-gray-600' : 'from-gray-500 to-gray-600';
  const platformIcon = platform ? platformIcons[platform] ?? '🎬' : '🎬';
  const platformLabel = (platform ?? 'OTHER').toUpperCase();
  const { toast } = useToast();

    const title = video.title || '';
  const creator = video.creator_name || '';
  const rawThumb = pickThumb(video, localPlacesPhoto);
  const providerFallback =
    preferImage((video as any).provider_thumbnail_url, video.video_url) ||
    preferImage((video as any).thumbnail_url as any, video.video_url) ||
    FALLBACK_THUMB;
  const thumb = rawThumb || null;
  
  // Debug photo URLs
  if (video.restaurant_name && video.restaurant_name !== 'N/A') {
    console.log('VideoCard photo debug:', {
      name: video.restaurant_name,
      places_photo_url: (video as any).places_photo_url,
      localPlacesPhoto,
      rawThumb,
      thumb
    });
  }

  // Defensive values (hide unknown)
  const views = typeof video.views === 'number' && video.views > 0 ? nf.format(video.views) : null;
  const likes = typeof video.likes === 'number' && video.likes > 0 ? nf.format(video.likes) : null;

  const hasRestaurant = Boolean(video.restaurant_name || video.restaurant_location || video.restaurant_rating || video.restaurant_price_range || video.restaurant_cuisine);
  const hasReviews = Array.isArray(video.reviews) && video.reviews.length > 0;
  const hasDish = Boolean(
    (video.extracted_dish_name && video.extracted_dish_name !== 'N/A') ||
    (Array.isArray(video.extracted_ingredients) && video.extracted_ingredients.length > 0) ||
    (typeof video.extracted_estimated_price === 'number' && video.extracted_estimated_price > 0)
  );

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    img.onerror = null; // prevent loops
  // Try provider thumbnail as a secondary, then SVG fallback
  img.src = providerFallback;
  };

  const safeTitle = decodeEntities(video.title || '');
  const preferRestaurant = (video.restaurant_name && video.restaurant_name !== 'N/A') ? video.restaurant_name : '';
  const displayTitle = preferRestaurant || safeTitle || (platform ? `${platform} Video` : 'Video');
  const altText = video.restaurant_name ? `Miniatura de ${video.restaurant_name}` : displayTitle;

  // Derived meta with client patch fallbacks
  const restaurantRating = (typeof metaPatch?.restaurant_rating === 'number' ? metaPatch.restaurant_rating : video.restaurant_rating) as number | undefined;
  const restaurantCuisine = (metaPatch?.restaurant_cuisine || video.restaurant_cuisine) as string | undefined;
  const restaurantAddress = (metaPatch?.restaurant_address || video.restaurant_address) as string | undefined;
  const deriveCity = (addr?: string): string | undefined => {
    if (!addr) return undefined;
    try {
      const parts = addr.split(',').map((p) => p.trim()).filter(Boolean);
      if (!parts.length) return undefined;
      const isCountry = (s: string) => /^(mexico|méxico|united states|usa|canada)$/i.test(s);
      const isState = (s: string) => /^(jal\.?|jalisco|cdmx|ciudad de méxico|nuevo león|edomex|estado de méxico|ny|ca|tx)$/i.test(s);
      const cleaned = parts.filter((p) => !isCountry(p) && !isState(p));
      const longText = cleaned.find((p) => /[A-Za-zÁ-ÿ]{3,}/.test(p) && !/\d{3,}/.test(p));
      return longText || cleaned[cleaned.length - 1] || parts[parts.length - 1];
    } catch { return undefined; }
  };
  const restaurantLocation = (video.restaurant_location && video.restaurant_location !== 'N/A')
    ? video.restaurant_location
    : (deriveCity(restaurantAddress) || video.restaurant_location);

  // Opening hours helpers
  const normalizeHours = (v: any): string[] => {
    try {
      if (Array.isArray(v)) return v.map((x) => String(x));
      if (typeof v === 'string') {
        const s = v.trim();
        if (s.startsWith('[') && s.endsWith(']')) {
          const arr = JSON.parse(s);
          return Array.isArray(arr) ? arr.map((x: any) => String(x)) : [];
        }
        if (s.includes('\n')) return s.split('\n').map((t) => t.trim()).filter(Boolean);
      }
    } catch {}
    return [];
  };
  const todayHoursText = React.useMemo(() => {
    const raw = (metaPatch?.opening_hours_weekday_text ?? (video as any).opening_hours_weekday_text) as any;
    const lines = normalizeHours(raw);
    if (lines.length === 0) return null;
    try {
      const dayName = new Date().toLocaleDateString(undefined, { weekday: 'long' });
      const match = lines.find((s) => s.toLowerCase().startsWith(dayName.toLowerCase()));
      const text = (match || lines[0]).replace(/^\s*[^:]+:\s*/i, '').trim();
      if (!text || /closed/i.test(text)) return null; // avoid redundant when closed
  return `${lang === 'en' ? 'Today' : 'Hoy'}: ${text}`;
    } catch {
      return null;
    }
  }, [video.opening_hours_weekday_text, metaPatch?.opening_hours_weekday_text]);

  const hoursTooltip = React.useMemo(() => {
    const raw = (metaPatch?.opening_hours_weekday_text ?? (video as any).opening_hours_weekday_text) as any;
    const lines = normalizeHours(raw);
    if (lines.length === 0) return '';
    try { return lines.join('\n'); } catch { return ''; }
  }, [video.opening_hours_weekday_text, metaPatch?.opening_hours_weekday_text]);

  // Lightweight client details fetch when initial rating/cuisine/hours are missing
  React.useEffect(() => {
    const pid = (video as any).enrich_place_id as string | undefined;
    const needRating = !(typeof video.restaurant_rating === 'number' && video.restaurant_rating > 0);
    const needHours = !(Array.isArray((video as any).opening_hours_weekday_text) && (video as any).opening_hours_weekday_text.length > 0);
    const needCuisine = !video.restaurant_cuisine || video.restaurant_cuisine === 'N/A';
    if (!pid || (!needRating && !needHours && !needCuisine)) return;
    (async () => {
      try {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        const fnBase = (process.env.REACT_APP_FUNCTIONS_BASE || '').replace(/\/$/, '');
        const url = new URL(`${fnBase}/places/details`, origin); url.searchParams.set('id', pid);
        const r = await fetch(url.toString()); if (!r.ok) return;
        const j = await r.json(); const d = j?.result || {};
        const types = Array.isArray(d.types) ? d.types.map((s: any) => String(s || '')).join(' ').toLowerCase() : '';
        const cuisine = types.match(/ramen|noodle/) ? 'Ramen' :
                        types.match(/sushi/) ? 'Sushi' :
                        types.match(/japanese/) ? (video.restaurant_cuisine ? `${video.restaurant_cuisine}` : 'Japanese') :
                        types.match(/mexican|taco|taqueria/) ? 'Mexican' : undefined;
        setMetaPatch({
          restaurant_rating: typeof d.rating === 'number' ? d.rating : undefined,
          opening_hours_weekday_text: Array.isArray(d.opening_hours?.weekday_text)
            ? d.opening_hours.weekday_text
            : (Array.isArray(d.opening_hours) ? d.opening_hours : undefined),
          open_now: typeof d.open_now === 'boolean'
            ? d.open_now
            : (typeof d.opening_hours?.open_now === 'boolean' ? d.opening_hours.open_now : undefined),
          restaurant_cuisine: cuisine || undefined,
          restaurant_address: d.formatted_address || undefined,
        });
      } catch {}
    })();
  }, [video.id]);

  // Lazily fetch a Google Places photo if missing and we have hints
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const hasPlaces = Boolean((video as any).places_photo_url);
      const hasHints = Boolean(
        (video.restaurant_name && video.restaurant_name !== 'N/A') ||
        (video.restaurant_address && video.restaurant_address !== 'N/A') ||
        (video.restaurant_location && video.restaurant_location !== 'N/A')
      );
      if (hasPlaces || !hasHints) return;
      try {
        const enriched = await enrichVideoWithPlaces(video, { minConfidence: 0.2 });
        const photo = (enriched as any).places_photo_url as string | null | undefined;
        if (!cancelled && photo) {
          setLocalPlacesPhoto(photo);
          (video as any).places_photo_url = photo; // persist in the object for ensuing renders
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [video.id]);

  // Bootstrap a place id for new items when missing (client-side search → details)
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const hasPid = !!(video as any).enrich_place_id;
      if (hasPid) return;
      const name = (video.restaurant_name && video.restaurant_name !== 'N/A') ? video.restaurant_name : '';
      const hint = video.restaurant_address || video.restaurant_location || '';
      if (!name && !hint) return;
      try {
        const q = [name, hint].filter(Boolean).join(' ');
        const results: any = await searchPlacesServer(q);
        const first = Array.isArray(results) ? results[0] : null;
        if (!first || cancelled) return;
        (video as any).enrich_place_id = first.place_id;
        (video as any).google_maps_url = `https://www.google.com/maps/place/?q=place_id:${first.place_id}`;
        try {
          const d: any = await getPlaceDetailsServer(first.place_id);
          if (!d || cancelled) return;
          setMetaPatch({
            restaurant_rating: typeof d.rating === 'number' ? d.rating : undefined,
            opening_hours_weekday_text: Array.isArray(d.opening_hours?.weekday_text)
              ? d.opening_hours.weekday_text
              : (Array.isArray(d.opening_hours) ? d.opening_hours : undefined),
            open_now: typeof d.open_now === 'boolean'
              ? d.open_now
              : (typeof d.opening_hours?.open_now === 'boolean' ? d.opening_hours.open_now : undefined),
            restaurant_cuisine: (metaPatch?.restaurant_cuisine || video.restaurant_cuisine) || undefined,
            restaurant_address: d.formatted_address || undefined,
          });
        } catch {}
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [video.id, video.restaurant_name, video.restaurant_address, video.restaurant_location]);

  const openDirectionsUrl = () => {
    const q = [video.restaurant_name, video.restaurant_address || video.restaurant_location].filter(Boolean).join(' ');
    const enc = encodeURIComponent(q);
    switch (directionsPref) {
      case 'apple':
        window.open(`http://maps.apple.com/?q=${enc}`, '_blank', 'noopener');
        break;
      case 'waze':
        window.open(`https://waze.com/ul?q=${enc}`, '_blank', 'noopener');
        break;
      case 'uber':
        window.open(`https://m.uber.com/ul/?action=setPickup&dropoff[formatted_address]=${enc}`,'_blank','noopener');
        break;
      default:
        window.open(video.google_maps_url && video.google_maps_url !== 'N/A' ? (video.google_maps_url as string) : `https://www.google.com/maps/search/?api=1&query=${enc}`, '_blank', 'noopener');
    }
  };

  const isNew = (() => {
    if (!video.created_at) return false;
    const days = (Date.now() - new Date(video.created_at).getTime()) / 86400000;
    return days <= 7;
  })();

  const trending = typeof video.views === 'number' && video.views > 10_000; // simple heuristic
  const isNearby = typeof (video as any).distance_km === 'number' && (video as any).distance_km < 3;

  const onTouchStart = () => {
    if (pressTimer) window.clearTimeout(pressTimer);
    const t = window.setTimeout(() => {
      // Long-press: toggle select and show mini menu via browser context menu (placeholder)
      if (video.id && onToggleSelect) onToggleSelect(video.id, !selected);
    }, 550);
    setPressTimer(t);
  };
  const onTouchEnd = () => { if (pressTimer) { window.clearTimeout(pressTimer); setPressTimer(null); } };

  return (
    <div
      className={`relative z-0 bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-shadow duration-300 group touch-pan-y select-none overscroll-contain hover:z-20 focus-within:z-20 ${selected ? 'ring-2 ring-blue-500' : ''} ${showMenu ? 'z-50' : ''}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClick={() => onVideoClick(video)}
    >
  <div className="relative aspect-video">

        {thumb ? (
          <>
            <img
              src={thumb}
              alt={altText}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              draggable={false}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={handleImgError}
              sizes="(min-width:1280px) 25vw, (min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw"
              className="w-full h-full object-cover"
            />
            {video.photo_attribution_html && (
              <div
                className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-1 rounded z-10"
                dangerouslySetInnerHTML={{ __html: sanitizeAttribution(video.photo_attribution_html) }}
              />
            )}
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 grid place-items-center">
            <span className="text-gray-500 text-sm flex items-center gap-2">
        {(platform ? platformIcons[platform] : '🎬') as any} {lang === 'en' ? 'No preview' : 'Vista previa no disponible'}
            </span>
          </div>
        )}

  {/* Stronger gradients for text legibility */}
  <div className="pointer-events-none absolute inset-0">
    {/* subtle global tint */}
    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-all duration-300" />
    {/* top gradient for platform chip/labels */}
    <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-black/50 via-black/20 to-transparent" />
    {/* bottom gradient for captions */}
    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/40 via-black/20 to-transparent" />
  </div>

  {/* Removed center play button; whole card is clickable */}

        {platform && (
          <div
            className={`absolute top-3 left-3 z-20 px-2.5 py-1 rounded-full bg-gradient-to-r ${badgeGradient} text-white text-xs font-semibold flex items-center gap-1 ring-1 ${platformRings[platform] || 'ring-white/60'} shadow-sm backdrop-blur-sm`}
            title={platform}
          >
            <span aria-hidden="true">{platformIcons[platform] ?? '🎬'}</span>
            <span className="tracking-wide">{platformShort[platform] || platform.toUpperCase()}</span>
            <span className="sr-only">Plataforma: {platform}</span>
          </div>
        )}

  <div className="absolute top-4 right-4 flex items-center gap-2 z-50 pointer-events-auto">
          {isNew && <span className="px-2 py-1 rounded bg-emerald-600 text-white text-[10px] uppercase tracking-wide">New</span>}
          {trending && <span className="px-2 py-1 rounded bg-orange-600 text-white text-[10px] uppercase tracking-wide">Trending</span>}
          {isNearby && <span className="px-2 py-1 rounded bg-blue-600 text-white text-[10px] uppercase tracking-wide">Nearby</span>}
          {views && (
            <div className="bg-black/50 backdrop-blur-sm rounded-lg px-2 py-1 text-white text-xs flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {views}
            </div>
          )}
          {likes && (
            <div className="bg-black/50 backdrop-blur-sm rounded-lg px-2 py-1 text-white text-xs flex items-center gap-1">
              <Heart className="w-3 h-3" />
              {likes}
            </div>
          )}
          {video.id && onToggleSelect && (
            <button
              className={`ml-1 w-6 h-6 rounded-full flex items-center justify-center ${selected ? 'bg-blue-600 text-white' : 'bg-white/90 text-gray-800'} shadow`}
              aria-label={selected ? 'Deselect' : 'Select for compare'}
              title={selected ? (lang === 'en' ? 'Deselect' : 'Quitar selección') : (lang === 'en' ? 'Select for compare' : 'Seleccionar para comparar')}
              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); onToggleSelect(video.id!, !selected); }}
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
          {/* Overflow menu */}
      <div className="relative z-40">
            <button
              className="ml-1 w-7 h-7 rounded-full flex items-center justify-center bg-white/90 text-gray-800 shadow hover:bg-white"
              aria-haspopup="menu"
              aria-expanded={showMenu}
              aria-controls={`video-menu-${video.id ?? index}`}
              aria-label="More"
              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setShowMenu((v)=>!v); }}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {showMenu && (
              <button
                aria-hidden
                className="fixed inset-0 z-40 cursor-default bg-transparent"
                onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setShowMenu(false); }}
              />
            )}
            {showMenu && (
        <div
          ref={menuPopupRef}
          id={`video-menu-${video.id ?? index}`}
          role="menu"
          aria-label={lang === 'en' ? 'Video actions' : 'Acciones del video'}
          className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg ring-1 ring-black/5 z-50 py-1 overflow-hidden outline-none"
        >
                <button
                  role="menuitem" tabIndex={0}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-800"
                  onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setShowMenu(false); setShowListPicker(true); }}
                >
                  <Bookmark className="w-4 h-4" /> {lang === 'en' ? 'Add to list' : 'Agregar a lista'}
                </button>
                <button
                  role="menuitem" tabIndex={-1}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-800"
                  onClick={async (e)=>{
                    e.preventDefault(); e.stopPropagation(); setShowMenu(false);
                    try {
                      await navigator.clipboard.writeText(video.video_url);
                      toast(lang === 'en' ? 'Video link copied' : 'Enlace del video copiado', 'success');
                    } catch { toast(lang === 'en' ? 'Could not copy' : 'No se pudo copiar', 'error'); }
                  }}
                >
                  <Link2 className="w-4 h-4" /> {lang === 'en' ? 'Copy video link' : 'Copiar enlace del video'}
                </button>
                {video.google_maps_url && video.google_maps_url !== 'N/A' && (
                  <button
                    role="menuitem" tabIndex={-1}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-800"
                    onClick={async (e)=>{
                      e.preventDefault(); e.stopPropagation(); setShowMenu(false);
                      try {
                        await navigator.clipboard.writeText(video.google_maps_url as string);
                        toast(lang === 'en' ? 'Maps link copied' : 'Enlace de Maps copiado', 'success');
                      } catch { toast(lang === 'en' ? 'Could not copy' : 'No se pudo copiar', 'error'); }
                    }}
                  >
                    <Copy className="w-4 h-4" /> {lang === 'en' ? 'Copy Maps link' : 'Copiar enlace de Maps'}
                  </button>
                )}
                <div className="h-px bg-gray-100 my-1" />
                <button
                  role="menuitem" tabIndex={-1}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  onClick={(e)=>{
                    e.preventDefault(); e.stopPropagation(); setShowMenu(false);
                    if (!video.id || !onDeleteVideo) return;
                    const vid = video.id;
                    // Simple delayed delete to allow an Undo toast in the future
                    toast(lang === 'en' ? 'Video deleted' : 'Video eliminado', 'info');
                    setTimeout(()=>{ onDeleteVideo(vid); }, 300);
                  }}
                >
                  <Trash2 className="w-4 h-4" /> {lang === 'en' ? 'Delete' : 'Eliminar'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Header: Source icon + date */}
        <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            {getPlatformIcon(video.platform || 'Other', "w-3.5 h-3.5")}
            <span className="capitalize">{(video.platform || 'Other').toLowerCase()}</span>
          </div>
          {video.created_at && (
            <span>{formatDateAdded(video.created_at)}</span>
          )}
        </div>

        {/* Hierarchy: Title + rating → Price • Cuisine → Location */}
        <div className="mb-1 flex items-center gap-2">
          <h3 className="font-bold text-lg text-gray-900 line-clamp-2 flex-1">
            {video.restaurant_name && video.restaurant_name !== 'N/A' ? video.restaurant_name : displayTitle}
          </h3>
          {(typeof restaurantRating === 'number' && restaurantRating > 0) && (
            <StarRating
              value={restaurantRating}
              count={(video as any).user_ratings_total ?? undefined}
              size="md"
              showNumber
            />
          )}
        </div>
        {/* Price • Cuisine tokens */}
        <div className="text-sm text-gray-700 mb-1 line-clamp-1">
          {(() => {
            const price = (video.restaurant_price_range && video.restaurant_price_range !== 'N/A') ? String(video.restaurant_price_range) : '';
            const cuisine = (restaurantCuisine && restaurantCuisine !== 'N/A') ? String(restaurantCuisine) : '';
            const tokens = [price, cuisine].filter(Boolean).join(' • ');
            return tokens || '';
          })()}
        </div>
        {/* Location below */}
        {restaurantLocation && restaurantLocation !== 'N/A' && (
          <div className="text-sm text-gray-600 mb-2 line-clamp-1">{restaurantLocation}</div>
        )}
        <div className="flex items-center gap-2 text-sm mb-3 flex-wrap">
          {(() => {
            const ratingToken = (typeof restaurantRating === 'number' && restaurantRating > 0)
              ? `★${Number(restaurantRating).toFixed(1)}`
              : null;
            const priceToken = (video.restaurant_price_range && video.restaurant_price_range !== 'N/A')
              ? video.restaurant_price_range
              : null;
            const km = (video as any).distance_km as number | undefined;
            const showDistance = typeof km === 'number' ? km <= 100 : Boolean((video as any).distance_label);
            const distToken = showDistance && (video as any).distance_label ? (video as any).distance_label : null;
            const platformToken = platform ? (platformShort[platform] || platform) : null;
            const meta = [ratingToken, priceToken, distToken, platformToken].filter(Boolean) as string[];
            const openNow = (metaPatch?.open_now ?? (video as any).open_now);
            const hasOpen = typeof openNow === 'boolean';
            const openIsTrue = openNow === true;
            const openToken = hasOpen ? (openIsTrue ? (lang === 'en' ? 'Open now' : 'Abierto ahora') : (lang === 'en' ? 'Closed' : 'Cerrado')) : null;
            return (
              <>
                {openToken && (
                  <span className={openIsTrue ? 'text-green-600' : 'text-red-600'}>{openToken}</span>
                )}
                {openToken && meta.length > 0 && (<span className="text-gray-400">•</span>)}
                {(hoursTooltip || todayHoursText) && (
                  <span className="inline-flex items-center gap-1 text-gray-700" title={hoursTooltip}>
                    <Clock className="w-3 h-3" />
                    {todayHoursText && <span className="hidden sm:inline">{todayHoursText}</span>}
                  </span>
                )}
                {(hoursTooltip || todayHoursText) && meta.length > 0 && (<span className="text-gray-400">•</span>)}
                {meta.map((t, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-gray-400">•</span>}
                    <span className="text-gray-700">{t}</span>
                  </React.Fragment>
                ))}
              </>
            );
          })()}
        </div>
        {todayHoursText && (
          <div className="text-xs text-gray-500 mb-3">{todayHoursText}</div>
        )}

        {hasRestaurant && (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                {video.restaurant_name && <h4 className="font-semibold text-gray-900">{video.restaurant_name}</h4>}
                {video.restaurant_location && <p className="text-gray-600 text-sm">{video.restaurant_location}</p>}

                {(video.restaurant_cuisine && video.restaurant_cuisine !== 'N/A') || hasReviews ? (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {video.restaurant_cuisine && video.restaurant_cuisine !== 'N/A' && (
                      <span className="text-sm text-gray-600">{video.restaurant_cuisine}</span>
                    )}
                    {video.restaurant_cuisine && hasReviews && (<span className="text-gray-400">•</span>)}
                    {hasReviews ? (
                      <button
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 underline"
                        onClick={(e) => { e.preventDefault(); onVideoClick(video); }}
                        aria-label={lang === 'en' ? 'See reviews' : 'Ver reseñas'}
                      >
                        {lang === 'en' ? 'See reviews' : 'Ver reseñas'}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {hasDish && (
              <div className="bg-gray-50 rounded-xl p-3">
                {video.extracted_dish_name && video.extracted_dish_name !== 'N/A' && (
                  <h5 className="font-medium text-gray-900 mb-1">{video.extracted_dish_name}</h5>
                )}
                {Array.isArray(video.extracted_ingredients) && video.extracted_ingredients.length > 0 && (
                  <p className="text-sm text-gray-600 mb-2">{video.extracted_ingredients.join(', ')}</p>
                )}

                <div className="flex justify-between items-center">
                  {(typeof video.extracted_estimated_price === 'number' && video.extracted_estimated_price > 0) ? (
                    <span className="text-sm font-semibold text-green-600">{video.extracted_estimated_price}</span>
                  ) : <span />}

                  {video.google_maps_url ? (
                    <motion.a
                      href={video.google_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 text-sm font-medium hover:text-blue-700"
                      whileHover={prefersReducedMotion ? undefined : { scale: 1.05 }}
                      whileTap={prefersReducedMotion ? undefined : { scale: 0.95 }}
                    >
                      Ver en Maps
                      <ExternalLink className="w-3 h-3" />
                    </motion.a>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick actions */}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={openDirectionsUrl}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700"
              aria-label={lang === 'en' ? 'Directions' : 'Cómo llegar'}
            >
              <MapPin className="w-4 h-4" /> {lang === 'en' ? 'Directions' : 'Cómo llegar'}
            </button>
            {(() => {
              const q = [video.restaurant_name, video.restaurant_address || video.restaurant_location].filter(Boolean).join(' ');
              const enc = encodeURIComponent(q);
              const googleHref = (video.google_maps_url && video.google_maps_url !== 'N/A') ? (video.google_maps_url as string) : `https://www.google.com/maps/search/?api=1&query=${enc}`;
              const appleHref = `http://maps.apple.com/?q=${enc}`;
              const wazeHref = `https://waze.com/ul?q=${enc}`;
              return (
                <div className="inline-flex items-center gap-1">
                  <a href={googleHref} target="_blank" rel="noopener noreferrer" title="Open in Google Maps" className="px-2 py-1 rounded-md text-gray-700 hover:bg-gray-100">Google</a>
                  <a href={appleHref} target="_blank" rel="noopener noreferrer" title="Open in Apple Maps" className="px-2 py-1 rounded-md text-gray-700 hover:bg-gray-100">Apple</a>
                  <a href={wazeHref} target="_blank" rel="noopener noreferrer" title="Open in Waze" className="px-2 py-1 rounded-md text-gray-700 hover:bg-gray-100">Waze</a>
                </div>
              );
            })()}
            <a
              href={video.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
              aria-label={lang === 'en' ? 'Open post' : 'Abrir publicación'}
            >
              <ExternalLink className="w-4 h-4" /> {lang === 'en' ? 'Open' : 'Abrir'}
            </a>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              type="button"
              className="relative px-2.5 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
              aria-label="Guardar"
              title="Guardar"
              onClick={(e) => { e.stopPropagation(); setShowListPicker((v)=>!v); }}
            >
              <span className={`inline-flex ${savedPulse ? 'animate-pulse text-green-600' : ''}`}><Bookmark className="w-5 h-5" /></span>
              {showListPicker && video.id && (
                <ListPicker videoId={video.id} onClose={()=>setShowListPicker(false)} onAdded={()=>{ setSavedPulse(true); setTimeout(()=>setSavedPulse(false), 1200); }} />
              )}
            </button>
            <button
              type="button"
              className="px-2.5 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
              aria-label="Compartir"
              title={shared === 'ok' ? 'Copiado!' : 'Compartir'}
              onClick={async () => {
                try {
                  const shareUrl = video.video_url || (video.google_maps_url as string);
                  if ((navigator as any).share) {
                    await (navigator as any).share({ title: displayTitle, url: shareUrl });
                  } else if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(shareUrl);
                    setShared('ok');
                    setTimeout(() => setShared(null), 1200);
                  }
                } catch {
                  setShared('err');
                  setTimeout(() => setShared(null), 1200);
                }
              }}
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={`px-2.5 py-2 rounded-lg hover:bg-gray-100 ${isFav ? 'text-red-600' : 'text-gray-700'}`}
              aria-label={isFav ? (lang === 'en' ? 'Unfavorite' : 'Quitar favorito') : (lang === 'en' ? 'Favorite' : 'Favorito')}
              title={isFav ? (lang === 'en' ? 'Unfavorite' : 'Quitar favorito') : (lang === 'en' ? 'Favorite' : 'Favorito')}
              onClick={(e) => { e.stopPropagation(); setIsFav((v) => !v); }}
            >
              <Heart className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* SR-only live region for share/save feedback */}
        <div aria-live="polite" className="sr-only">
          {shared === 'ok' ? (lang === 'en' ? 'Link copied' : 'Enlace copiado') :
           shared === 'err' ? (lang === 'en' ? 'Share failed' : 'No se pudo compartir') :
           savedPulse ? (lang === 'en' ? 'Saved' : 'Guardado') : ''}
        </div>
      </div>
    </div>
  );
};

export default VideoCard;